import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Flag } from 'lucide-react';

const MAX_REASON = 500;

/**
 * Unobtrusive flag/report button for a Pro service listing (W3). POSTs {reason} to the
 * PUBLIC, rate-limited /api/pro-listings/{id}/report — it works logged-out, so a Bearer
 * token is attached only if a session happens to exist. Success shows a neutral toast.
 *
 * @param {string} listingId
 */
const ReportListingButton = ({ listingId }) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!reason.trim()) {
      toast.error(t('services.reportReasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      // Public endpoint — attach a token only when logged in (optional).
      let headers;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          headers = { Authorization: `Bearer ${session.access_token}` };
        }
      } catch (sessionError) {
        console.error('[ReportListingButton] session lookup failed (reporting anonymously):', sessionError);
        headers = undefined;
      }

      await api.post(
        `/api/pro-listings/${listingId}/report`,
        { reason: reason.trim() },
        headers ? { headers } : undefined
      );

      toast.success(t('services.reportSuccess'));
      setShowDialog(false);
      setReason('');
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 429) {
        toast.error(detail || t('services.reportRateLimited'));
      } else {
        toast.error(detail || t('services.reportError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
        onClick={() => setShowDialog(true)}
        data-testid="report-listing-button"
      >
        <Flag className="w-4 h-4 mr-2" aria-hidden="true" />
        {t('services.reportListing')}
      </Button>

      <Dialog open={showDialog} onOpenChange={(open) => { if (!submitting) setShowDialog(open); }}>
        <DialogContent data-testid="report-listing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag size={18} className="text-red-600" aria-hidden="true" />
              {t('services.reportTitle')}
            </DialogTitle>
            <DialogDescription>{t('services.reportBody')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="report-reason">{t('services.reportReasonLabel')}</Label>
            <Textarea
              id="report-reason"
              value={reason}
              maxLength={MAX_REASON}
              rows={4}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('services.reportReasonPlaceholder')}
              data-testid="report-reason-textarea"
            />
            <p className="text-xs text-muted-foreground text-right">{reason.length}/{MAX_REASON}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={submitting}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !reason.trim()} data-testid="submit-report-listing-button">
              {submitting ? t('services.reportSubmitting') : t('services.reportSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportListingButton;
